import { useState } from 'react'
import { LeaderMetrics } from '../../../types/metrics'
import InfoTooltip from '../../atoms/InfoTooltip'
import DashboardCardToolbar from './DashboardCardToolbar'

const LEADERS_BREAKDOWN_TOOLTIP =
  'Per-leader summary of reports reviewed, average score, and rank mix across their team.'

type Props = {
  leaders: LeaderMetrics[]
  onExport: () => Promise<void>
}

const RANK_SEGMENTS = [
  { key: 'tenX' as const, color: '#15803D', label: '10X (Top talent)' },
  { key: 'nineX' as const, color: '#4ADE80', label: '9X (Growth / Expert)' },
  { key: 'core' as const, color: '#FDE047', label: 'Core talent' },
  { key: 'mismatch' as const, color: '#FCA5A5', label: 'Mismatch' },
]

type RankKey = 'tenX' | 'nineX' | 'core' | 'mismatch'

/** Mini stacked bar showing rank mix for a single leader. */
const RankMiniBar = (leader: Pick<LeaderMetrics, RankKey>): JSX.Element => {
  const total = leader.tenX + leader.nineX + leader.core + leader.mismatch
  if (total === 0) return <span className="text-xl text-stone-gray">–</span>

  const pct = (n: number): number => Math.round((n / total) * 100)

  return (
    <div className="flex items-center gap-2">
      <div className="flex h-5 w-40 overflow-hidden rounded-md">
        {RANK_SEGMENTS.filter(s => leader[s.key] > 0).map(s => (
          <div
            key={s.key}
            style={{ width: `${pct(leader[s.key])}%`, backgroundColor: s.color }}
            title={`${s.label}: ${leader[s.key]} (${pct(leader[s.key])}%)`}
          />
        ))}
      </div>
      <span className="whitespace-nowrap text-xl text-stone-gray">
        {leader.tenX}/{leader.nineX}/{leader.core}/{leader.mismatch}
      </span>
    </div>
  )
}

const LEADER_PAGE_SIZE = 10
const SEARCH_ID = 'leader-search'

const LeaderBreakdownTable = ({ leaders, onExport }: Props): JSX.Element => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')

  const filtered = leaders.filter(l => !search || l.name.toLowerCase().includes(search.toLowerCase()))
  const totalPages = Math.ceil(filtered.length / LEADER_PAGE_SIZE)
  const safePage = Math.min(page, Math.max(0, totalPages - 1))
  const pagedLeaders = filtered.slice(safePage * LEADER_PAGE_SIZE, (safePage + 1) * LEADER_PAGE_SIZE)

  if (leaders.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
        <div className="mb-4 flex items-center gap-2">
          <h3 className="text-2xl font-bold text-dark-blue">Leaders breakdown</h3>
          <InfoTooltip text={LEADERS_BREAKDOWN_TOOLTIP} />
        </div>
        <p className="text-xl text-stone-gray">No leaders with completed reviews found.</p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
      <DashboardCardToolbar
        title="Leaders breakdown"
        tooltipText={LEADERS_BREAKDOWN_TOOLTIP}
        search={{
          id: SEARCH_ID,
          value: search,
          onChange: value => {
            setSearch(value)
            setPage(0)
          },
          placeholder: 'Search by leader name…',
          ariaLabel: 'Search leaders by name',
          className: 'w-96',
        }}
        onExport={onExport}
      />

      <div className="overflow-x-auto">
        <table className="w-full" aria-label="Leaders breakdown">
          <thead>
            <tr className="border-b-2 border-soft-gray">
              <th className="pb-3 text-left text-xl font-semibold text-blue-gray">Leader</th>
              <th className="pb-3 text-center text-xl font-semibold text-blue-gray">Reports reviewed</th>
              <th className="pb-3 text-center text-xl font-semibold text-blue-gray">Avg score</th>
              <th className="relative pb-3 text-left text-xl font-semibold text-blue-gray">
                <button
                  className="inline-flex items-center gap-1 hover:text-dark-blue"
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  onFocus={() => setShowTooltip(true)}
                  onBlur={() => setShowTooltip(false)}
                  aria-describedby="rank-mix-tooltip"
                  type="button"
                >
                  Rank mix
                  <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-soft-gray text-[10px] font-bold text-stone-gray">
                    ?
                  </span>
                </button>
                {showTooltip && (
                  <div
                    id="rank-mix-tooltip"
                    role="tooltip"
                    className="absolute left-0 top-full z-10 mt-1 w-64 rounded-lg border border-soft-gray bg-white p-3 text-xl font-normal text-blue-gray shadow-lg"
                  >
                    <p className="mb-2 font-semibold text-dark-blue">How reports under this leader were ranked:</p>
                    {RANK_SEGMENTS.map(s => (
                      <div key={s.key} className="mb-1 flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                        {s.label}
                      </div>
                    ))}
                    <p className="mt-2 text-stone-gray">Numbers show 10X / 9X / Core / Mismatch counts.</p>
                  </div>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedLeaders.map(leader => (
              <tr key={leader.id} className="odd:bg-gray-cell even:bg-gray-cell/50 hover:bg-just-gray">
                <td className="py-4 pr-4 text-xl font-semibold text-dark-blue">{leader.name}</td>
                <td className="py-4 text-center text-xl text-blue-gray">{leader.reportCount}</td>
                <td className="py-4 text-center text-xl text-blue-gray">
                  {leader.avgScore !== null ? `${leader.avgScore}` : '–'}
                </td>
                <td className="py-4">
                  <RankMiniBar tenX={leader.tenX} nineX={leader.nineX} core={leader.core} mismatch={leader.mismatch} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <nav
          aria-label="Leaders table pagination"
          className="mt-4 flex items-center justify-between text-xl text-stone-gray"
        >
          <span aria-live="polite">
            {filtered.length} result{filtered.length !== 1 ? 's' : ''} · page {safePage + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              disabled={safePage === 0}
              onClick={() => setPage(p => p - 1)}
              aria-label="Previous page"
              className="rounded border border-soft-gray px-3 py-1 hover:bg-just-gray disabled:opacity-40"
            >
              ‹ Prev
            </button>
            <button
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
              aria-label="Next page"
              className="rounded border border-soft-gray px-3 py-1 hover:bg-just-gray disabled:opacity-40"
            >
              Next ›
            </button>
          </div>
        </nav>
      )}
    </div>
  )
}

export const LeaderBreakdownTableSkeleton = (): JSX.Element => (
  <div aria-hidden="true" className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
    <div className="mb-4 flex items-center justify-between">
      <div className="h-7 w-1/3 rounded bg-soft-gray animate-pulse" />
      <div className="h-9 w-48 rounded bg-soft-gray animate-pulse" />
    </div>
    <div className="flex flex-col gap-3">
      {[0, 1, 2, 3, 4, 5].map(i => (
        <div key={i} className="flex items-center gap-4">
          <div className="h-6 flex-1 rounded bg-soft-gray animate-pulse" />
          <div className="h-6 w-20 rounded bg-soft-gray animate-pulse" />
          <div className="h-6 w-20 rounded bg-soft-gray animate-pulse" />
          <div className="h-4 w-40 rounded-full bg-soft-gray animate-pulse" />
        </div>
      ))}
    </div>
  </div>
)

export default LeaderBreakdownTable
